import Store from 'store/store';
import { UNIVERSAL_STUDIO_SLUG, FOCUS_STUDIO_SLUG } from 'utils/constants';
import { isPlaceholder } from 'utils/data';
import { getCurrentRegionSettings, isBaseRegion, isFocusStudio } from 'utils/studioConfig';

import MovieInfoEditor from 'components/editor/movies/movieInfo';
import MovieMediaEditor from 'components/editor/movies/movieMedia';
import MoviePageEditor from 'components/editor/movies/moviePage';
import MovieSidebar from 'components/editor/movies/movieSidebar';

import AboutEditor from 'components/editor/pages/about';

import UniversalLandingEditor from 'components/editor/pages/landing';
import UniversalLeadershipEditor from 'components/editor/pages/leadership';
import UniversalNewsEditor from 'components/editor/pages/news';

import FocusContactEditor from 'components/editor/pages/focus/contact';
import FocusLandingEditor from 'components/editor/pages/focus/landing';

const pageEditors = {
	about: AboutEditor,
	[UNIVERSAL_STUDIO_SLUG]: {
		landing: UniversalLandingEditor,
		news: UniversalNewsEditor,
		leadership: UniversalLeadershipEditor,
	},
	[FOCUS_STUDIO_SLUG]: {
		contact: FocusContactEditor,
		landing: FocusLandingEditor,
	},
};

export function getMovieData(id) {
	const movies = Store.get().data?.movies || {};
	const localeMovie = movies[id];
	if (localeMovie) return localeMovie;
	const baseMovie = Object.values(movies).find(movie => movie.base_movie_id === id);
	return baseMovie || null;
}

/** Data path of the item currently being edited, or null when not in an editor */
export function getCurrentDataPath() {
	// Not ideal, we should unify how the data path is computed across components
	const navPath = Store.get().path;
	if (navPath[0] === 'navigation') {
		return '/navigation';
	}
	if (navPath.length > 1 && (navPath[0] === 'pages' || navPath[0] === 'movies')) {
		const config = getEditorConfigFromURL(navPath);
		const pathStr = navPath.join('/');
		const section = config?.sections?.find(section => pathStr.startsWith(`${config.basePath}/${section.path}`));
		return section?.dataRoot || null;
	}
	return null;
}

// This should be somewhere else
export async function showCropper() {
	document.getElementById('cropper')?.showModal();
}

export async function hideCropper() {
	document.getElementById('cropper')?.close();
}

export function getEditorConfigFromURL(path) {
	const store = Store.get();
	const loadedData = store.data;
	const studioSlug = store.domains.studioSlug;
	const regionCode = store.domains.regionCode;
	const isFocus = isFocusStudio();

	if (path[0] === 'pages') {
		const slug = path[1];
		const page = Object.values(loadedData.pages).find((page) => page.slug === slug);
		if (!page) return;

		const Editor = pageEditors[`${studioSlug}:${regionCode}`]?.[slug] || pageEditors[studioSlug]?.[slug] || pageEditors[slug];
		return {
			id: page.id,
			slug,
			basePath: `pages/${slug}`,
			title: page.name,
			sections: [
				{
					path: 'edit',
					name: 'Page editor',
					content: Editor,
					dataRoot: `/pages/${page.id}`,
				},
			],
		};
	}

	if (path[0] === 'movies') {
		const id = path[1];
		const movie = getMovieData(id);
		if (!movie) return;

		const movieId = movie.id;
		const placeholder = isPlaceholder(movie);
		const isBase = isBaseRegion();
		const homeEntEnabled = getCurrentRegionSettings()?.homeEnt;

		return {
			id: movieId,
			placeholder,
			basePath: `movies/${id}`,
			title: movie.content?.title || movie.title,
			sidebar: MovieSidebar,
			sections: [
				{
					path: 'info',
					name: 'Movie info',
					content: MovieInfoEditor,
					dataRoot: `/movies/${movieId}`,
				},
				// Show media editor for all Focus movies, and non-placeholder Universal movies
				(isBase || isFocus || !placeholder) && {
					path: 'media',
					name: 'Media',
					content: MovieMediaEditor,
					dataRoot: `/movies/${movieId}`,
				},
				// Focus placeholders: Simple "Movie page" link (no sub-pages)
				(isFocus && placeholder) && {
					path: 'pages/home',
					name: 'Movie page',
					dataRoot: `/movies/${movieId}`,
					content: MoviePageEditor,
				},
				// Published movies: "Pages" section with sub-pages
				(!isBase && !placeholder) && {
					path: 'pages',
					name: 'Pages',
					dataRoot: `/movies/${movieId}`,
					content: MoviePageEditor,
					pages: [
						{
							name: 'Landing',
							path: 'home',
							dataRoot: '/home',
							// Focus Features: no sub-sections (single unified landing page)
							// Universal: separate Synopsis, Gallery, Videos sections
							pageSections: isFocus ? undefined : [
								{ id: 'synopsis', name: 'Synopsis' },
								{ id: 'gallery', name: 'Gallery' },
								{ id: 'videos', name: 'Videos' },
							],
						},
						{ name: 'Showtimes', path: 'showtimes', dataRoot: '/showtimes', badges: [{ type: 'live', path: '/active' }] },
						homeEntEnabled && { name: 'HomeEnt', path: 'home-ent', dataRoot: '/homeEnt', badges: [{ type: 'live', path: '/active' }] },
					].filter(Boolean),
				},
			].filter(Boolean),
		};
	}
}
